import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import type { UniqueIdentifier } from '@lumapps/lumx-dnd/types';
import {
    ItemRef,
    SortableListItem,
    SortableListItemProps,
} from '@lumapps/lumx-lists/components/SortableList/SortableListItem';
import { FlexBox, Orientation, Alignment, DragHandle } from '@lumapps/lumx/react';

import { SORTABLE_CLASSNAME } from '../../constants';

import './index.scss';

const CLASSNAME = 'wrex-dnd-children';
const SCOPE = 'wrex-dnd';

export interface DragAndDropChildrenProps extends React.PropsWithChildren {
    /** index of the element in the list */
    index: number;
    /** unique id of the element in drag and drop context */
    id: UniqueIdentifier;
    /** wrex element type */
    type: string;
    /** true if the element is currently being dragged */
    isDragged: boolean;
    /** children already rendered by slate */
    renderedChildren: React.JSX.Element;
    /** scope for data-ids */
    scope?: string;
    /** ref for the list item */
    itemRef?: (el: ItemRef) => void;
    /** the color of the RTE's background */
    backgroundColor?: string /* hex only */;
}

export const DragAndDropChildren = ({
    type,
    renderedChildren,
    isDragged,
    id,
    index,
    itemRef,
    scope,
    backgroundColor,
}: DragAndDropChildrenProps) => {
    const { element } = useClassnames(CLASSNAME);
    const { get } = useDataAttributes(scope ? `${scope}-${SCOPE}` : SCOPE);

    const entityRenderer: SortableListItemProps<React.JSX.Element>['entityRenderer'] = React.useCallback(
        (entity, { dragHandleProps, isDragOngoing, isActive }) => (
            <FlexBox
                orientation={Orientation.horizontal}
                hAlign={Alignment.center}
                className={element('drag-wrapper', {
                    placeholder: isActive,
                })}
                {...get({ element: `${type}-drag-wrapper` })}
            >
                <DragHandle
                    {...dragHandleProps}
                    // Drag and drop is disabled on keyboard for now
                    tabIndex={-1}
                    className={element('drag-handle', {
                        dragging: isDragOngoing,
                        dragged: isActive,
                        'with-background': Boolean(backgroundColor),
                    })}
                    {...get({ element: `${type}-drag-handle` })}
                />
                {entity}
            </FlexBox>
        ),
        [backgroundColor, element, get, type],
    );

    return (
        <SortableListItem
            className={SORTABLE_CLASSNAME}
            entity={renderedChildren}
            index={index}
            id={id}
            isDragged={isDragged}
            mode="vertical"
            gap="huge"
            entityRenderer={entityRenderer}
            withDragHandle
            withDirectMovement={false}
            itemRef={itemRef}
        />
    );
};
